import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Pencil, Fingerprint, Users, CalendarClock, Loader2, Trash2, CalendarDays, FileSpreadsheet, BarChart3, Link2 } from 'lucide-react';
import { AdminAbsencesTab } from '../../components/hr/AdminAbsencesTab';
import { AdminReportTab } from '../../components/hr/AdminReportTab';
import { YearSummary } from '../../components/hr/YearSummary';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('hw_token')}` });

const DAYS = [['mon', 'Lun'], ['tue', 'Mar'], ['wed', 'Mer'], ['thu', 'Gio'], ['fri', 'Ven'], ['sat', 'Sab'], ['sun', 'Dom']];
const DEFAULT_SCHEDULE_TEXT = { mon: '09:00-13:00, 14:00-18:00', tue: '09:00-13:00, 14:00-18:00', wed: '09:00-13:00, 14:00-18:00', thu: '09:00-13:00, 14:00-18:00', fri: '09:00-13:00, 14:00-18:00', sat: '', sun: '' };

const scheduleToText = (s) => {
  const out = {};
  DAYS.forEach(([k]) => { out[k] = (s?.[k] || []).map(r => `${r[0]}-${r[1]}`).join(', '); });
  return out;
};
const textToSchedule = (t) => {
  const out = {};
  DAYS.forEach(([k]) => {
    out[k] = (t[k] || '').split(',').map(x => x.trim()).filter(Boolean).map(x => x.split('-').map(y => y.trim())).filter(r => r.length === 2);
  });
  return out;
};

const emptyForm = () => ({ name: '', email: '', temp_password: '', role_type: 'customer_service', ferie_balance_days: 0, rol_balance_hours: 0, ferie_month_days: 2.16, rol_month_hours: 4.33, contract_weekly_hours: 40, hired_date: '', schedule_text: { ...DEFAULT_SCHEDULE_TEXT } });

export default function GestionePersonale() {
  const [emps, setEmps] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null); // {id?} + emptyForm
  const [saving, setSaving] = useState(false);
  const [adjust, setAdjust] = useState(null); // {id, field, delta, note}
  const [yearView, setYearView] = useState(null); // {id, name}
  const [moduleEnabled, setModuleEnabled] = useState(false);
  const [range, setRange] = useState(() => {
    const to = new Date().toISOString().slice(0, 10);
    const from = new Date(Date.now() - 6 * 86400000).toISOString().slice(0, 10);
    return { from, to };
  });

  const load = useCallback(async () => {
    try {
      const [e, a, s] = await Promise.all([
        axios.get(`${API}/hr/employees`, { headers: authHeaders() }),
        axios.get(`${API}/hr/attendance?date_from=${range.from}&date_to=${range.to}`, { headers: authHeaders() }),
        axios.get(`${API}/hr/settings`, { headers: authHeaders() }),
      ]);
      setEmps(e.data.employees || []);
      setRows(a.data.rows || []);
      setModuleEnabled(!!s.data.module_enabled);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Errore caricamento'); }
    finally { setLoading(false); }
  }, [range]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.name || !form.email) { toast.error('Nome ed email obbligatori'); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name, email: form.email, role_type: form.role_type,
        weekly_schedule: textToSchedule(form.schedule_text),
        ferie_balance_days: form.ferie_balance_days, rol_balance_hours: form.rol_balance_hours,
        ferie_month_days: form.ferie_month_days, rol_month_hours: form.rol_month_hours,
        contract_weekly_hours: form.contract_weekly_hours,
        hired_date: form.hired_date || null,
      };
      if (form.id) {
        await axios.put(`${API}/hr/employees/${form.id}`, payload, { headers: authHeaders() });
        toast.success('Dipendente aggiornato');
      } else {
        const r = await axios.post(`${API}/hr/employees`, { ...payload, temp_password: form.temp_password }, { headers: authHeaders() });
        if (r.data.temp_password) {
          toast.success(`Account creato! Password temporanea: ${r.data.temp_password} — comunicala al dipendente (dovrà cambiarla al primo accesso)`, { duration: 30000 });
        } else {
          toast.success(r.data.user_created ? 'Dipendente creato con account di accesso' : 'Dipendente creato (account già esistente collegato)');
        }
      }
      setForm(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore salvataggio'); }
    finally { setSaving(false); }
  };

  const toggleModule = async () => {
    try {
      const r = await axios.put(`${API}/hr/settings`, { module_enabled: !moduleEnabled }, { headers: authHeaders() });
      setModuleEnabled(r.data.module_enabled);
      toast.success(r.data.module_enabled ? 'Modulo ATTIVATO: ora i dipendenti possono timbrare' : 'Modulo disattivato: visibile solo a te');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
  };

  const resetPasskey = async (id) => {
    await axios.post(`${API}/hr/employees/${id}/reset-passkey`, {}, { headers: authHeaders() });
    toast.success('Passkey azzerate: il dipendente registrerà il nuovo telefono');
    load();
  };

  const createInvite = async (e) => {
    try {
      const r = await axios.post(`${API}/hr/employees/${e.id}/invite`, {}, { headers: authHeaders() });
      await navigator.clipboard.writeText(r.data.url);
      toast.success(`Link per ${r.data.employee_name} copiato! Incollalo su WhatsApp (valido ${r.data.expires_days} giorni, monouso)`);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Errore nella creazione del link'); }
  };

  const removeEmployee = async (e) => {
    if (!window.confirm(`Eliminare ${e.name}? Se ha timbrature storiche verrà solo disattivato.`)) return;
    try {
      const r = await axios.delete(`${API}/hr/employees/${e.id}`, { headers: authHeaders() });
      toast.success(r.data.mode === 'eliminato' ? 'Dipendente eliminato' : 'Dipendente disattivato (storico conservato)');
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Errore'); }
  };

  const saveAdjust = async () => {
    try {
      await axios.post(`${API}/hr/employees/${adjust.id}/balance-adjust`,
        { field: adjust.field, delta: parseFloat(adjust.delta), note: adjust.note }, { headers: authHeaders() });
      toast.success('Saldo rettificato');
      setAdjust(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
  };

  const fmtH = (m) => `${Math.floor((m || 0) / 60)}h ${(m || 0) % 60}m`;
  const punchAt = (r, type, idx) => {
    const p = (r.punches || []).find(x => x.type === type && x.range_index === idx);
    if (!p) return '—';
    const t = new Date(p.ts).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
    return p.auto ? `${t}·auto` : t;
  };

  if (loading) return <div className="p-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;

  return (
    <div className="p-6 space-y-6" data-testid="gestione-personale-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Gestione Personale</h1>
          <p className="text-sm text-slate-500">Dipendenti, timbrature, ferie e permessi</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" data-testid="hr-guide-btn" onClick={() => window.open('/guida-timbratura', '_blank')}>
            📖 Guida dipendenti
          </Button>
          <Button data-testid="hr-toggle-module-btn" variant={moduleEnabled ? 'outline' : 'default'}
            className={moduleEnabled ? 'border-emerald-300 text-emerald-700' : 'bg-emerald-600 hover:bg-emerald-500'}
            onClick={toggleModule}>
            {moduleEnabled ? '🟢 Attivo per i dipendenti — Disattiva' : '⚪ Non attivo — Attiva per i dipendenti'}
          </Button>
          <Button data-testid="hr-add-employee-btn" onClick={() => setForm(emptyForm())}>
            <Plus className="h-4 w-4 mr-1" /> Nuovo dipendente
          </Button>
        </div>
      </div>

      <Tabs defaultValue="employees">
        <TabsList>
          <TabsTrigger value="employees" data-testid="hr-tab-employees"><Users className="h-4 w-4 mr-1" /> Dipendenti</TabsTrigger>
          <TabsTrigger value="attendance" data-testid="hr-tab-attendance"><CalendarClock className="h-4 w-4 mr-1" /> Presenze</TabsTrigger>
          <TabsTrigger value="absences" data-testid="hr-tab-absences"><CalendarDays className="h-4 w-4 mr-1" /> Ferie e Permessi</TabsTrigger>
          <TabsTrigger value="report" data-testid="hr-tab-report"><FileSpreadsheet className="h-4 w-4 mr-1" /> Report Paghe</TabsTrigger>
        </TabsList>

        <TabsContent value="employees" className="space-y-3 mt-4">
          {emps.length === 0 && <p className="text-slate-500 text-sm" data-testid="hr-no-employees">Nessun dipendente: aggiungi il primo con "Nuovo dipendente".</p>}
          {emps.map(e => (
            <div key={e.id} className="border border-slate-200 rounded-xl p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`hr-emp-${e.id}`}>
              <div>
                <p className="font-semibold text-slate-800">{e.name} {e.active === false && <span className="text-xs text-rose-500">(cessato)</span>}</p>
                <p className="text-xs text-slate-500">{e.email} · {e.role_type === 'programmatore' ? '💻 Programmatore' : '🎧 Customer Service'}</p>
                <p className="text-xs text-slate-400 mt-1">
                  Ferie: <b className="text-emerald-600">{Number(e.ferie_balance_days || 0).toFixed(2)} gg</b> ·
                  Permessi: <b className="text-sky-600">{Number(e.rol_balance_hours || 0).toFixed(2)} h</b> ·
                  Maturazione: +{e.ferie_month_days}gg/+{e.rol_month_hours}h al mese ·
                  Contratto: <b className="text-slate-600">{Number(e.contract_weekly_hours ?? 40)}h/sett</b> ·
                  <Fingerprint className="h-3 w-3 inline mx-1" />{e.passkeys > 0 ? `${e.passkeys} passkey` : 'nessuna passkey'}
                </p>
              </div>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" data-testid={`hr-edit-${e.id}`}
                  onClick={() => setForm({ id: e.id, ...emptyForm(), name: e.name, email: e.email, role_type: e.role_type, ferie_balance_days: e.ferie_balance_days, rol_balance_hours: e.rol_balance_hours, ferie_month_days: e.ferie_month_days, rol_month_hours: e.rol_month_hours, contract_weekly_hours: e.contract_weekly_hours ?? 40, hired_date: e.hired_date || '', schedule_text: scheduleToText(e.weekly_schedule) })}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" data-testid={`hr-adjust-${e.id}`} onClick={() => setAdjust({ id: e.id, name: e.name, field: 'ferie_balance_days', delta: '', note: '' })}>± Saldo</Button>
                <Button variant="outline" size="sm" data-testid={`hr-year-${e.id}`} title="Riepilogo annuale assenze" onClick={() => setYearView({ id: e.id, name: e.name })}>
                  <BarChart3 className="h-4 w-4 text-indigo-600" />
                </Button>
                <Button variant="outline" size="sm" data-testid={`hr-invite-${e.id}`} title="Genera link di registrazione telefono (da girare su WhatsApp)" onClick={() => createInvite(e)}>
                  <Link2 className="h-4 w-4 mr-1" /> Link telefono
                </Button>
                <Button variant="outline" size="sm" data-testid={`hr-reset-pk-${e.id}`} title="Azzera passkey (cambio telefono)" onClick={() => resetPasskey(e.id)}>
                  <Fingerprint className="h-4 w-4 text-rose-500" />
                </Button>
                <Button variant="ghost" size="sm" data-testid={`hr-delete-${e.id}`} title="Elimina dipendente" onClick={() => removeEmployee(e)}>
                  <Trash2 className="h-4 w-4 text-rose-500" />
                </Button>
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="attendance" className="space-y-3 mt-4">
          <div className="flex gap-2 items-end">
            <div><Label className="text-xs">Dal</Label><Input type="date" value={range.from} onChange={e => setRange({ ...range, from: e.target.value })} data-testid="hr-att-from" /></div>
            <div><Label className="text-xs">Al</Label><Input type="date" value={range.to} onChange={e => setRange({ ...range, to: e.target.value })} data-testid="hr-att-to" /></div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="hr-attendance-table">
              <thead><tr className="text-left text-xs text-slate-500 border-b">
                <th className="py-2 pr-3">Giorno</th><th className="pr-3">Dipendente</th>
                <th className="pr-3">Entrata</th><th className="pr-3">Pausa</th><th className="pr-3">Rientro</th><th className="pr-3">Uscita</th>
                <th className="pr-3">Ritardi</th><th className="pr-3">Anticipi</th><th>Ore</th>
              </tr></thead>
              <tbody>
                {rows.length === 0 && <tr><td colSpan={9} className="py-6 text-center text-slate-400">Nessuna timbratura nel periodo</td></tr>}
                {rows.map((r, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="py-2 pr-3">{new Date(r.day).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' })}</td>
                    <td className="pr-3 font-medium">{r.employee_name}</td>
                    <td className="pr-3">{punchAt(r, 'in', 0)}</td>
                    <td className="pr-3">{punchAt(r, 'out', 0)}</td>
                    <td className="pr-3">{punchAt(r, 'in', 1)}</td>
                    <td className="pr-3">{punchAt(r, 'out', 1)}</td>
                    <td className="pr-3">{r.late_minutes > 0 ? <span className="text-rose-600 font-medium">{r.late_minutes}m</span> : '—'}</td>
                    <td className="pr-3">{r.early_exit_minutes > 0 ? <span className="text-amber-600 font-medium">{r.early_exit_minutes}m</span> : '—'}</td>
                    <td>{fmtH(r.worked_minutes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="absences">
          <AdminAbsencesTab emps={emps} />
        </TabsContent>

        <TabsContent value="report">
          <AdminReportTab />
        </TabsContent>
      </Tabs>

      {/* Dialog crea/modifica */}
      {form && (
        <Dialog open onOpenChange={v => !v && setForm(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="hr-employee-dialog">
            <DialogHeader><DialogTitle>{form.id ? 'Modifica dipendente' : 'Nuovo dipendente'}</DialogTitle>
              <DialogDescription>Anagrafica, saldi, maturazione e orario settimanale</DialogDescription></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Nome e cognome</Label><Input data-testid="hr-f-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label className="text-xs">Email (login)</Label><Input data-testid="hr-f-email" value={form.email} disabled={!!form.id} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
              {!form.id && <div><Label className="text-xs">Password temporanea (vuota = generata)</Label><Input data-testid="hr-f-password" value={form.temp_password} onChange={e => setForm({ ...form, temp_password: e.target.value })} /></div>}
              <div><Label className="text-xs">Ruolo</Label>
                <select data-testid="hr-f-role" className="w-full border rounded-md h-9 px-2 text-sm" value={form.role_type} onChange={e => setForm({ ...form, role_type: e.target.value })}>
                  <option value="customer_service">Customer Service (regola 2 su 4)</option>
                  <option value="programmatore">Programmatore (libero nel maturato)</option>
                </select>
              </div>
              <div><Label className="text-xs">Data assunzione</Label><Input type="date" data-testid="hr-f-hired" value={form.hired_date} onChange={e => setForm({ ...form, hired_date: e.target.value })} /></div>
            </div>
            <p className="text-xs font-semibold text-slate-600 mt-2">Saldi attuali, maturazione mensile e contratto</p>
            <div className="grid grid-cols-5 gap-3">
              <div><Label className="text-xs">Ferie residue (gg)</Label><Input type="number" step="0.01" data-testid="hr-f-ferie" value={form.ferie_balance_days} onChange={e => setForm({ ...form, ferie_balance_days: e.target.value })} /></div>
              <div><Label className="text-xs">Permessi residui (h)</Label><Input type="number" step="0.01" data-testid="hr-f-rol" value={form.rol_balance_hours} onChange={e => setForm({ ...form, rol_balance_hours: e.target.value })} /></div>
              <div><Label className="text-xs">+Ferie gg/mese</Label><Input type="number" step="0.01" data-testid="hr-f-ferie-month" value={form.ferie_month_days} onChange={e => setForm({ ...form, ferie_month_days: e.target.value })} /></div>
              <div><Label className="text-xs">+Permessi h/mese</Label><Input type="number" step="0.01" data-testid="hr-f-rol-month" value={form.rol_month_hours} onChange={e => setForm({ ...form, rol_month_hours: e.target.value })} /></div>
              <div><Label className="text-xs">Ore/sett contratto</Label><Input type="number" step="0.5" data-testid="hr-f-contract-hours" value={form.contract_weekly_hours} onChange={e => setForm({ ...form, contract_weekly_hours: e.target.value })} /></div>
            </div>
            <p className="text-xs font-semibold text-slate-600 mt-2">Orario settimanale (es. "09:00-13:00, 14:00-18:00" — vuoto = riposo)</p>
            <div className="grid grid-cols-1 gap-1">
              {DAYS.map(([k, label]) => (
                <div key={k} className="flex items-center gap-2">
                  <span className="w-10 text-xs text-slate-500">{label}</span>
                  <Input data-testid={`hr-f-sched-${k}`} className="h-8 text-sm" value={form.schedule_text[k]}
                    onChange={e => setForm({ ...form, schedule_text: { ...form.schedule_text, [k]: e.target.value } })} />
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <Button variant="ghost" onClick={() => setForm(null)}>Annulla</Button>
              <Button data-testid="hr-f-save" onClick={save} disabled={saving}>{saving ? 'Salvataggio…' : 'Salva'}</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Dialog riepilogo annuale assenze */}
      {yearView && (
        <Dialog open onOpenChange={v => !v && setYearView(null)}>
          <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="hr-year-dialog">
            <DialogHeader><DialogTitle>📊 Riepilogo annuale — {yearView.name}</DialogTitle>
              <DialogDescription>Ferie, permessi e malattie godute per anno</DialogDescription></DialogHeader>
            <YearSummary empId={yearView.id} light />
          </DialogContent>
        </Dialog>
      )}

      {/* Dialog rettifica saldo */}
      {adjust && (
        <Dialog open onOpenChange={v => !v && setAdjust(null)}>
          <DialogContent className="max-w-sm" data-testid="hr-adjust-dialog">
            <DialogHeader><DialogTitle>Rettifica saldo — {adjust.name}</DialogTitle>
              <DialogDescription>Variazione manuale con nota (tracciata nei movimenti)</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <select className="w-full border rounded-md h-9 px-2 text-sm" value={adjust.field} onChange={e => setAdjust({ ...adjust, field: e.target.value })} data-testid="hr-adj-field">
                <option value="ferie_balance_days">Ferie (giorni)</option>
                <option value="rol_balance_hours">Permessi (ore)</option>
              </select>
              <div><Label className="text-xs">Variazione (+ aggiunge, − toglie)</Label>
                <Input type="number" step="0.01" data-testid="hr-adj-delta" value={adjust.delta} onChange={e => setAdjust({ ...adjust, delta: e.target.value })} placeholder="es. -1.5" /></div>
              <div><Label className="text-xs">Nota</Label>
                <Input data-testid="hr-adj-note" value={adjust.note} onChange={e => setAdjust({ ...adjust, note: e.target.value })} placeholder="es. ferie godute ad agosto" /></div>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setAdjust(null)}>Annulla</Button>
                <Button data-testid="hr-adj-save" onClick={saveAdjust}>Applica</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
